'use client';
import classNames from 'classnames';
import { isH5HandleClient } from 'eco-design-micro/utils-base/isH5Handle';
import getLocal from 'eco-design-reliance/getLocal';
import gtm from 'eco-design-reliance/gtm';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import { useEffect, useId, useRef, useState } from 'react';
import { Autoplay, EffectFade, Navigation } from 'swiper';
import dynamic from 'next/dynamic';
import { Swiper, SwiperSlide } from 'swiper/react';
import CardImgSecond from './img/CardImgSecond';
import { VideoComp } from './VideoDialog';
import { getUrlGtm } from '@/utils/gtmUtils';

export const getFontColorByBg = (theme: string, isOpposite = false): string => {
    // 将 theme 统一转为 16 进制
    function parseToHex(t: string): string {
        if (!t) return '000000';
        t = t.trim().toLowerCase();
        // 支持常见颜色名称
        const colorNameMap: Record<string, string> = {
            black: '000000',
            white: 'ffffff',
            red: 'ff0000',
            green: '00ff00',
            blue: '0000ff',
            yellow: 'ffff00',
            gray: '808080',
            // 可根据需要增加其他常见颜色
        };
        if (colorNameMap[t]) return colorNameMap[t];
        if (t[0] === '#') t = t.slice(1);
        // 短 3 位，如 #000 或 #abc
        if (t.length === 3 && /^[0-9a-f]{3}$/.test(t)) {
            t = t[0] + t[0] + t[1] + t[1] + t[2] + t[2];
        }
        // 6 位 HEX
        if (t.length === 6 && /^[0-9a-f]{6}$/.test(t)) return t;
        // RGB 或 RGBA 格式
        const rgbMatch = t.match(/^rgb *\((\d{1,3}), *(\d{1,3}), *(\d{1,3})\)$/);
        const rgbaMatch = t.match(/^rgba *\((\d{1,3}), *(\d{1,3}), *(\d{1,3}), *[0-9\.]+\)$/);
        if (rgbMatch || rgbaMatch) {
            const m = rgbMatch || rgbaMatch;
            const r = Math.max(0, Math.min(255, Number(m?.[1])));
            const g = Math.max(0, Math.min(255, Number(m?.[2])));
            const b = Math.max(0, Math.min(255, Number(m?.[3])));
            return [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
        }
        // fallback 默认黑
        return '000000';
    }
    const hex = parseToHex(String(theme));

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // YIQ公式判断亮度
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    if (isOpposite) {
        return yiq >= 128 ? 'white' : 'black';
    }
    return yiq >= 128 ? 'black' : 'white';
};

const VideoDialog: any = dynamic(() => import('./VideoDialog'), { ssr: true });
interface Props {
    initIndex: number;
    autoPlay: boolean;
    list: any;
    prefixCls: string;
    className?: string;
    tabLayout: string;
    size: 'default' | 'small';
}

export const BannerCom: React.FC<Props> = ({ initIndex, autoPlay, list, tabLayout, size, prefixCls, className, ...reset }) => {
    const { locale = 'us' } = getLocal();
    const swiperRef = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState(initIndex);
    const itemSlideChange = (swiperA: any) => {
        setActiveIndex(swiperA.realIndex);
    };

    const uuid = useId().replaceAll(':', 'a');

    const [isH5, setIsH5] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        setIsH5(isH5HandleClient());
    }, []);

    const switchGtm = (position: string, name: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'banner_switch',
            position: `Homepage_topbanner_${position}`,
            button_name: name,

            __btn_id1: `kv3-swiper-control-${position}`,
            __btn_id2: `control-${position}`,
            __btn_type: 'common',
        });
    };

    const [videoDialog, setVideoDialog] = useState(false);
    const [videoDialog2, setVideoDialog2] = useState(false);

    return (
        <Swiper
            className={`${className} index-banner-swiper-compent ${className}-${uuid}`}
            onSlideChange={itemSlideChange}
            initialSlide={initIndex}
            pagination={false}
            navigation={{
                nextEl: `.${className}-${uuid} .swiper-button-next`,
                prevEl: `.${className}-${uuid} .swiper-button-prev`,
            }}
            loop={(list?.length ?? 0) > 1}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            speed={1000}
            allowTouchMove={isH5}
            autoplay={
                autoPlay
                    ? {
                          delay: 3000, // 轮播间隔时间（毫秒），默认3000
                          disableOnInteraction: false, // 用户操作后是否停止自动轮播，false表示不停止
                      }
                    : false
            }
            modules={[Navigation, Autoplay, EffectFade]}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            key={
                !isH5
                    ? autoPlay
                        ? `${className}-autoplay-on`
                        : `${className}-autoplay-off`
                    : autoPlay
                    ? `${className}-autoplay-on-h5`
                    : `${className}-autoplay-off-h5`
            }
        >
            {list.map((itemA: any, index: number) => {
                const {
                    pc,
                    h5,
                    label,
                    title,
                    desc,
                    theme = 'white',
                    layout,
                    lHref,
                    lBtnContent,
                    rHref,
                    rBtnContent,
                    isVideo,
                    lBtnType,
                    lBtnVideoType = 'upload',
                    lBtnVideoPCUrl,
                    rBtnVideoType = 'upload',
                    rBtnVideoPCUrl,
                    lBtnVideoPc,
                    lBtnVideoH5,
                    rBtnType,
                    rBtnVideoPc,
                    rBtnVideoH5,
                } = itemA;
                const itemClassesA = classNames({
                    [`${prefixCls}-item`]: true,
                    [`${prefixCls}-item-${index}`]: true,
                    [`${prefixCls}-theme-${theme}`]: theme,
                    [size]: true,
                });

                const openLink = lHref && lBtnType === 'link' ? lHref : rHref && rBtnType === 'link' ? rHref : '';
                const linkLGtm = {
                    ...getUrlGtm(lHref, locale, {
                        list_name: 'Banner',
                        product_name: title,
                        button_name: lBtnContent,
                    }),
                    position: `Homepage_topbanner_${lBtnContent}`,

                    __btn_id1: `kv3-l-btn-${index}`,
                    __btn_id2: lHref,
                    __btn_type: 'href',
                };
                const linkRGtm = {
                    ...getUrlGtm(rHref, locale, {
                        list_name: 'Banner',
                        product_name: title,
                        button_name: rBtnContent,
                    }),
                    position: `Homepage_topbanner_${rBtnContent}`,

                    __btn_id1: `kv3-r-btn-${index}`,
                    __btn_id2: rHref,
                    __btn_type: 'href',
                };

                return (
                    <>
                        <SwiperSlide
                            className={itemClassesA}
                            style={{
                                // @ts-ignore
                                '--kv-banner-theme-color': theme,
                                '--kv-banner-theme-font-color': getFontColorByBg(theme),
                            }}
                            key={`${index}`}
                            onClick={() => {
                                if (openLink) {
                                    window.open(openLink, '_self');
                                    gtm.push(lHref && lBtnType === 'link' ? linkLGtm : linkRGtm);
                                }
                            }}
                        >
                            <div className={`${prefixCls}-item-img`}>
                                {!isVideo && (
                                    <>
                                        {index === 0 && <EcoImg pc={pc} h5={h5} />}
                                        {index !== 0 && <CardImgSecond prefixCls={prefixCls} pc={pc} h5={h5} />}
                                    </>
                                )}
                                {isVideo && <VideoComp pc={pc} h5={h5} />}
                            </div>
                            <div className={`${prefixCls}-item-msg`}>
                                <div className={`${prefixCls}-item-info ${layout}`}>
                                    {label && <div className={`${prefixCls}-item-label`}>{label}</div>}
                                    {title && <div className={`${prefixCls}-item-title`}>{title}</div>}
                                    {desc && <div className={`${prefixCls}-item-desc`}>{desc}</div>}
                                    <div className={`${prefixCls}-box-btn`}>
                                        <EcoButton
                                            className={`GtmModuleData-btn-${lBtnType === 'link' ? 'href' : 'common'} kv3-l-btn-${index}`}
                                            stopPropagation
                                            data-btn-id={`item-l-${index + 1}`}
                                            lType="filled"
                                            lRadius="ellipse"
                                            lHref={lBtnType === 'link' ? lHref : undefined}
                                            lChildren={lBtnContent}
                                            lDataLayer={lBtnType === 'link' ? linkLGtm : undefined}
                                            onClick={() => {
                                                if (lBtnType === 'video') {
                                                    setVideoDialog(true);
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'video',
                                                        title_name: title,
                                                        position: 'Product Detail_1',

                                                        __btn_id1: `kv3-l-btn-${index}`,
                                                        __btn_id2: `item-l-${index + 1}`,
                                                        __btn_type: 'common',
                                                    });
                                                }
                                            }}
                                        />
                                        <EcoButton
                                            className={`GtmModuleData-btn-${rBtnType === 'link' ? 'href' : 'common'} kv3-r-btn-${index}`}
                                            stopPropagation
                                            data-btn-id={`item-r-${index + 1}`}
                                            lType="outlined"
                                            lRadius="ellipse"
                                            lHref={rBtnType === 'link' ? rHref : undefined}
                                            lChildren={rBtnContent}
                                            lIsWithRightArrow={true}
                                            lDataLayer={rBtnType === 'link' ? linkRGtm : undefined}
                                            onClick={() => {
                                                if (rBtnType === 'video') {
                                                    setVideoDialog2(true);
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'video',
                                                        title_name: title,
                                                        position: 'Product Detail_1',

                                                        __btn_id1: `kv3-r-btn-${index}`,
                                                        __btn_id2: `item-r-${index + 1}`,
                                                        __btn_type: 'common',
                                                    });
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            {lBtnType === 'video' && (
                                <VideoDialog
                                    pc={lBtnVideoType === 'upload' ? lBtnVideoPc : lBtnVideoPCUrl}
                                    h5={lBtnVideoType === 'upload' ? lBtnVideoH5 : lBtnVideoPCUrl}
                                    open={videoDialog}
                                    onClose={() => setVideoDialog(false)}
                                />
                            )}
                            {rBtnType === 'video' && (
                                <VideoDialog
                                    pc={rBtnVideoType === 'upload' ? rBtnVideoPc : rBtnVideoPCUrl}
                                    h5={rBtnVideoType === 'upload' ? rBtnVideoH5 : rBtnVideoPCUrl}
                                    open={videoDialog2}
                                    onClose={() => setVideoDialog2(false)}
                                />
                            )}
                        </SwiperSlide>
                    </>
                );
            })}
            {(list?.length ?? 0) > 1 && (
                <>
                    <div className={`${prefixCls}-control`}>
                        <div
                            className={`swiper-button-prev GtmModuleData-btn-common kv3-swiper-control-Left`}
                            data-btn-id="control-Left"
                            onClick={() => switchGtm('Left', 'Left')}
                        ></div>
                        <div
                            className={`swiper-button-next GtmModuleData-btn-common kv3-swiper-control-Right`}
                            data-btn-id="control-Right"
                            onClick={() => switchGtm('Right', 'Right')}
                        ></div>
                    </div>
                    <ul className={`${prefixCls}-pagination ${prefixCls}-pagination-${tabLayout}`}>
                        {list.map((itemB: any, index: number) => {
                            const itemClassesB = classNames({
                                [`${prefixCls}-pagination-item`]: true,
                                [`${prefixCls}-pagination-active`]: index === activeIndex,
                            });
                            return (
                                <li
                                    key={index}
                                    className={`${itemClassesB} GtmModuleData-btn-common kv3-swiper-control-Bottom-${index + 1}`}
                                    data-btn-id={`control-Bottom-${index + 1}`}
                                    onClick={() => {
                                        if (swiperRef.current) {
                                            swiperRef.current.slideTo(index + 1);
                                            setActiveIndex(index);
                                        }
                                        switchGtm(`Bottom-${index + 1}`, itemB.tab_name);
                                    }}
                                >
                                    <div className={`${prefixCls}-pagination-title`}>{itemB.tab_name}</div>
                                    <div className={`${prefixCls}-pagination-line`}></div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </Swiper>
    );
};
